import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

const API = import.meta.env.VITE_API_URL || '/api'
function getHeaders() { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', Authorization: t ? `Bearer ${t}` : '' } }

const FONT_OPTIONS = [
  'Inter', 'Poppins', 'Roboto', 'Montserrat', 'Playfair Display',
  'Raleway', 'DM Sans', 'Nunito', 'Plus Jakarta Sans', 'Open Sans',
  'Lora', 'Merriweather', 'Oswald', 'Source Sans Pro',
]

export default function AdminNavigation() {
  const [data, setData] = useState<any>(null)
  const [links, setLinks] = useState<any[]>([])
  const [siteName, setSiteName] = useState('')
  const [siteTagline, setSiteTagline] = useState('')
  const [siteDescription, setSiteDescription] = useState('')
  const [siteUrl, setSiteUrl] = useState('')
  const [siteLogo, setSiteLogo] = useState('')
  const [siteFavicon, setSiteFavicon] = useState('')
  const [headingFont, setHeadingFont] = useState('Inter')
  const [bodyFont, setBodyFont] = useState('Inter')
  const [ctaLabel, setCtaLabel] = useState('')
  const [ctaPath, setCtaPath] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    fetch(`${API}/navigation`).then(r => r.json()).then(d => {
      setData(d)
      setLinks(d.links || [])
      setSiteName(d.site_name || '')
      setSiteTagline(d.site_tagline || '')
      setSiteDescription(d.site_description || '')
      setSiteUrl(d.site_url || '')
      setSiteLogo(d.site_logo || '')
      setSiteFavicon(d.site_favicon || '')
      setHeadingFont(d.heading_font || 'Inter')
      setBodyFont(d.body_font || 'Inter')
      setCtaLabel(d.cta_label || '')
      setCtaPath(d.cta_path || '')
      setLoading(false)
    })
  }, [])

  const addLink = () => setLinks([...links, { label: '', path: '' }])
  const updateLink = (i: number, key: string, value: string) => {
    const copy = [...links]
    copy[i] = { ...copy[i], [key]: value }
    setLinks(copy)
  }
  const removeLink = (i: number) => setLinks(links.filter((_, idx) => idx !== i))

  const handleSave = async () => {
    setSaving(true)
    const r = await fetch(`${API}/navigation`, {
      method: 'PUT', headers: getHeaders(),
      body: JSON.stringify({
        links: links.filter(l => l.label && l.path),
        cta_label: ctaLabel, cta_path: ctaPath,
        site_name: siteName, site_tagline: siteTagline,
        site_description: siteDescription, site_url: siteUrl,
        site_logo: siteLogo, site_favicon: siteFavicon,
        heading_font: headingFont, body_font: bodyFont,
      }),
    })
    if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 3000) }
    setSaving(false)
  }

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Navigation & Site Settings</h1>
      {saved && <div className="mb-4 px-4 py-3 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg text-sm">Saved successfully!</div>}

      <div className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-900 border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Site Metadata</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Site Name</label>
              <input value={siteName} onChange={e => setSiteName(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Tagline</label>
              <input value={siteTagline} onChange={e => setSiteTagline(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-400 mb-2">Description</label>
              <textarea value={siteDescription} onChange={e => setSiteDescription(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Site URL</label>
              <input value={siteUrl} onChange={e => setSiteUrl(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Logo Path</label>
              <input value={siteLogo} onChange={e => setSiteLogo(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Favicon Path</label>
              <input value={siteFavicon} onChange={e => setSiteFavicon(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-gray-900 border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Font Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Heading Font</label>
              <select value={headingFont} onChange={e => setHeadingFont(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue appearance-none">
                {FONT_OPTIONS.map(f => (
                  <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: headingFont }}>Preview: The quick brown fox jumps over the lazy dog</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Body Font</label>
              <select value={bodyFont} onChange={e => setBodyFont(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue appearance-none">
                {FONT_OPTIONS.map(f => (
                  <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: bodyFont }}>Preview: The quick brown fox jumps over the lazy dog</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gray-900 border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Navigation Links</h2>
            <button onClick={addLink} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors">+ Add Link</button>
          </div>
          {links.map((link, i) => (
            <div key={i} className="flex items-center gap-3 mb-3">
              <input value={link.label} onChange={e => updateLink(i, 'label', e.target.value)} placeholder="Label" className="flex-1 px-4 py-2.5 bg-gray-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-electric-blue" />
              <input value={link.path} onChange={e => updateLink(i, 'path', e.target.value)} placeholder="/path" className="flex-1 px-4 py-2.5 bg-gray-800 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-electric-blue" />
              <button onClick={() => removeLink(i)} className="px-3 py-2.5 text-red-400 hover:text-red-300 transition-colors">✕</button>
            </div>
          ))}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-gray-900 border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">CTA Button</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Button Label</label>
              <input value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Button Path</label>
              <input value={ctaPath} onChange={e => setCtaPath(e.target.value)} className="w-full px-4 py-3 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-electric-blue" />
            </div>
          </div>
        </motion.div>

        <button onClick={handleSave} disabled={saving} className="px-6 py-3 bg-electric-blue hover:bg-electric-blue/80 text-white font-medium rounded-lg transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  )
}
